/**
 * NDA Admin API Endpoints
 * The agreement that gates the app: its versions, its text, and the acceptance record.
 *
 * `/admin/*` paths are rewritten to the Admin API's `/api/*` — see api/client.ts. That host
 * deliberately never runs the NDA gate itself, so this panel keeps answering even when the
 * agreement is in a state that locks everyone else out.
 */

import { ApiService } from '../apiService'
import type {
  NdaAcceptanceDto,
  NdaCurrentResponse,
  NdaVersionDto,
  SaveNdaRequest,
  SaveNdaResponse,
} from '../types/nda'

export const ndaApi = {
  /** Every version, newest first, each with the number of acceptances recorded against it. */
  getVersions: () => ApiService.get<NdaVersionDto[]>('/admin/nda/versions'),

  /** The version in force, or `{ published: false }` when none has ever been saved. */
  getCurrent: () => ApiService.get<NdaCurrentResponse>('/admin/nda/current'),

  /**
   * Publishes the text. Only mints a version when the normalised text differs from the current
   * one — see `newVersion` on the response before telling anybody what just happened.
   */
  save: (request: SaveNdaRequest) => ApiService.put<SaveNdaResponse>('/admin/nda', request),

  /** The acceptance record, optionally narrowed to one version. */
  getAcceptances: (version?: number) =>
    ApiService.get<NdaAcceptanceDto[]>(
      '/admin/nda/acceptances',
      version === undefined ? undefined : { version },
    ),
}
